import React, { forwardRef } from 'react';
import {
  Paper,
  Grid,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  Button,
  Box,
  Typography,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  computeCutoffElapsedHours,
  formatCutoffTimeOfDay,
  formatElapsedShort,
  formatLegCutoffPace,
} from '../../utils/formatters';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FlagIcon from '@mui/icons-material/Flag';

const AidStationItem = forwardRef(({
  index,
  station,
  onChange,
  onRemove,
  onOpenItemsDialog,
  error,
  unit = 'miles',
  startTimeInfo = null,
  prevCutoffElapsedHours = 0,
  prevCutoffLocation = 0,
}, ref) => {
  // Handle changes to a field in the aid station
  const handleChange = (field, value) => {
    // If it's a location field and not a finish location, ensure we store it in the current unit
    if (field === 'location' && !station.isFinish) {
      // Store original information about the value and its unit for later conversion if needed
      onChange(index, 'originalLocation', value);
      onChange(index, 'originalUnit', unit);
    }
    
    onChange(index, field, value);
  };

  // Handle checkbox changes
  const handleCheckboxChange = (field) => (event) => {
    const checked = event.target.checked;
    if (field === 'hasCutoffTime' && checked && !station.cutoffAMPM) {
      handleChange('cutoffAMPM', 'AM');
    }
    handleChange(field, checked);
  };

  // Render finish location
  if (station.isFinish) {
    return (
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          mb: 2, 
          position: 'relative',
          borderLeft: '4px solid #4caf50'
        }}
        ref={ref}
      >
        <IconButton
          size="small"
          onClick={() => onRemove(index)}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <FlagIcon sx={{ color: '#4caf50', mr: 2, fontSize: 32 }} />
          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Finish Line
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {station.location} {unit}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={station.hasLocation || false}
                onChange={handleCheckboxChange('hasLocation')}
              />
            }
            label="Add Finish Line Address"
          />
          
          {station.hasLocation && (
            <TextField
              fullWidth
              label="Finish Line Address"
              value={station.locationAddress || ''}
              onChange={(e) => handleChange('locationAddress', e.target.value)}
              placeholder="e.g., Race Finish Line, City Park"
              sx={{ mt: 2 }}
              InputProps={{
                endAdornment: station.locationAddress && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(station.locationAddress)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: '#1976d2' }}
                    >
                      <Box component="span" sx={{ fontSize: '0.75rem', mr: 0.5 }}>Map</Box>
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )}

          <TextField
            fullWidth
            label="Notes"
            value={station.notes || ''}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="e.g., Parking available in main lot, finish line festivities in pavilion"
            multiline
            rows={2}
            sx={{ mt: 3 }}
            InputProps={{
              inputProps: {
                maxLength: 500,
              },
            }}
            helperText={
              <Box 
                component="span" 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  mt: 0.5
                }}
              >
                {(station.notes || '').length} / 500
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={station.hasCrew}
                onChange={handleCheckboxChange('hasCrew')}
              />
            }
            label="Crew Access"
            sx={{ mt: 2 }}
          />
        </Box>
      </Paper>
    );
  }

  // Render regular aid station
  return (
    <Paper 
      elevation={1} 
      sx={{ 
        p: 2, 
        mb: 2, 
        position: 'relative',
        borderLeft: station.hasDropBag ? '4px solid #124E66' : 'none'
      }}
      ref={ref}
    >
      <IconButton
        size="small"
        onClick={() => onRemove(index)}
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
      
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`Location (${unit})`}
                value={station.location}
                onChange={(e) => {
                  // Make sure the value is a valid number
                  const value = e.target.value;
                  if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                    handleChange('location', value);
                  }
                }}
                placeholder={`e.g., 25.3 ${unit}`}
                required
                error={!!error}
                helperText={error || `Distance in ${unit}`}
                type="text"
                InputProps={{
                  inputProps: {
                    inputMode: "numeric",
                    pattern: "[0-9]*.?[0-9]*",
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {unit}
                      </Typography>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                fullWidth
                label="Aid Station Name"
                value={station.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Twin Lakes"
                sx={{ mt: 2 }}
                InputProps={{
                  inputProps: {
                    maxLength: 50,
                  },
                }}
              />

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Planned stop duration (race clock time at this aid)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    label="Hours (HH)"
                    value={station.aidStopDurationHours ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^[0-9]+$/.test(value)) {
                        handleChange('aidStopDurationHours', value);
                      }
                    }}
                    size="small"
                    sx={{ width: 100 }}
                    inputProps={{ inputMode: 'numeric', min: 0 }}
                    placeholder="0"
                  />
                  <Typography sx={{ pb: 1, fontWeight: 600 }}>:</Typography>
                  <TextField
                    label="Minutes (MM)"
                    value={station.aidStopDurationMinutes ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        handleChange('aidStopDurationMinutes', '');
                        return;
                      }
                      if (!/^[0-9]+$/.test(value)) return;
                      const n = parseInt(value, 10);
                      if (n >= 0 && n < 60) {
                        handleChange('aidStopDurationMinutes', value);
                      }
                    }}
                    size="small"
                    sx={{ width: 110 }}
                    inputProps={{ inputMode: 'numeric', min: 0, max: 59 }}
                    placeholder="00"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Example: two hours at this aid → Hours 2, Minutes 0 (2:00). Long crew stops or sleep count here.
                  Goal pace reflects time left for running after all planned stops.
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={station.hasCutoffTime || false}
                    onChange={handleCheckboxChange('hasCutoffTime')}
                  />
                }
                label="Add Cut-off Time"
                sx={{ mt: 2, display: 'block' }}
              />

              {station.hasCutoffTime && (() => {
                const elapsedHours = computeCutoffElapsedHours(
                  station,
                  startTimeInfo,
                  prevCutoffElapsedHours
                );
                const hasStartTime = !!startTimeInfo;
                const hasValue =
                  (station.cutoffHours !== '' && station.cutoffHours != null) ||
                  (station.cutoffMinutes !== '' && station.cutoffMinutes != null);
                const paceUnitAbbr = unit === 'kilometers' ? 'km' : 'mi';
                const loc = parseFloat(station.location);
                let fromStartPaceText = '';
                let legPaceText = '';
                if (
                  elapsedHours != null &&
                  hasValue &&
                  Number.isFinite(loc) &&
                  loc > 0
                ) {
                  fromStartPaceText = formatLegCutoffPace(
                    (elapsedHours * 60) / loc,
                    paceUnitAbbr
                  );
                  const legElapsed = elapsedHours - prevCutoffElapsedHours;
                  const legDist = loc - prevCutoffLocation;
                  if (legDist > 0.01 && legElapsed > 0) {
                    legPaceText = formatLegCutoffPace(
                      (legElapsed * 60) / legDist,
                      paceUnitAbbr
                    );
                  }
                }
                return (
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Cut-off time of day at this aid station
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                      <TextField
                        label="Hour"
                        value={station.cutoffHours ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            handleChange('cutoffHours', value);
                            return;
                          }
                          if (!/^[0-9]+$/.test(value)) return;
                          const n = parseInt(value, 10);
                          if (n >= 1 && n <= 12) {
                            handleChange('cutoffHours', value);
                          }
                        }}
                        size="small"
                        placeholder="12"
                        InputProps={{
                          inputProps: {
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            min: 1,
                            max: 12,
                          },
                        }}
                        sx={{ width: '80px' }}
                      />
                      <Typography sx={{ pt: 1, fontWeight: 600 }}>:</Typography>
                      <TextField
                        label="Min"
                        value={station.cutoffMinutes ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            handleChange('cutoffMinutes', value);
                            return;
                          }
                          if (!/^[0-9]+$/.test(value)) return;
                          const n = parseInt(value, 10);
                          if (n >= 0 && n < 60) {
                            handleChange('cutoffMinutes', value);
                          }
                        }}
                        size="small"
                        placeholder="00"
                        InputProps={{
                          inputProps: {
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            min: 0,
                            max: 59,
                          },
                        }}
                        sx={{ width: '90px' }}
                      />
                      <FormControl size="small" sx={{ minWidth: '80px' }}>
                        <InputLabel id={`cutoff-ampm-${index}`}>AM/PM</InputLabel>
                        <Select
                          labelId={`cutoff-ampm-${index}`}
                          label="AM/PM"
                          value={station.cutoffAMPM || 'AM'}
                          onChange={(e) => handleChange('cutoffAMPM', e.target.value)}
                        >
                          <MenuItem value="AM">AM</MenuItem>
                          <MenuItem value="PM">PM</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Typography
                      variant="caption"
                      color={elapsedHours == null ? 'text.secondary' : 'primary'}
                      display="block"
                      sx={{ mt: 1, fontWeight: elapsedHours == null ? 400 : 600 }}
                    >
                      {!hasStartTime
                        ? 'Set a race start time on the Race Details step to see elapsed time.'
                        : !hasValue
                          ? 'Example: enter 11:45 AM to set the cutoff at that clock time.'
                          : elapsedHours == null
                            ? ''
                            : [
                                `${formatCutoffTimeOfDay(station)} → ${formatElapsedShort(elapsedHours)} after start`,
                                legPaceText ? `From previous cut: ${legPaceText}` : null,
                                fromStartPaceText ? `From start: ${fromStartPaceText}` : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                    </Typography>
                  </Box>
                );
              })()}
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={station.hasLocation || false}
                    onChange={handleCheckboxChange('hasLocation')}
                  />
                }
                label="Add Address/Coordinates"
                sx={{ mt: 2 }}
              />
              {station.hasLocation && (
                <TextField
                  fullWidth
                  label="Address or Coordinates"
                  value={station.locationAddress || ''}
                  onChange={(e) => handleChange('locationAddress', e.target.value)}
                  placeholder="e.g., 123 Main St, City, State or 40.7128° N, 74.0060° W"
                  sx={{ mt: 1 }}
                  InputProps={{
                    endAdornment: station.locationAddress && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(station.locationAddress)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: '#1976d2' }}
                        >
                          <Box component="span" sx={{ fontSize: '0.75rem', mr: 0.5 }}>Map</Box>
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              )}
              <TextField
                fullWidth
                label="Notes"
                value={station.notes || ''}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="e.g., Crew access via dirt road, limited parking"
                multiline
                rows={2}
                sx={{ mt: 2 }}
                InputProps={{
                  inputProps: {
                    maxLength: 500,
                  },
                }}
                helperText={
                  <Box 
                    component="span" 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-end',
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      mt: 0.5
                    }}
                  >
                    {(station.notes || '').length} / 500
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={station.hasDropBag}
                      onChange={handleCheckboxChange('hasDropBag')}
                    />
                  }
                  label="Drop Bag"
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={station.hasPacer}
                      onChange={handleCheckboxChange('hasPacer')}
                    />
                  }
                  label="Pacer Pickup"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={station.hasCrew}
                      onChange={handleCheckboxChange('hasCrew')}
                    />
                  }
                  label="Crew Access"
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
        
        {!station.isFinish && station.hasPacer && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Pacer Name"
              value={station.pacerName || ''}
              onChange={(e) => handleChange('pacerName', e.target.value)}
              placeholder="Enter pacer's name"
              InputProps={{
                startAdornment: <DirectionsRunIcon sx={{ color: 'action.active', mr: 1 }} />,
              }}
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
            />
          </Grid>
        )}
        
        {!station.isFinish && station.hasDropBag && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ mr: 1 }}>
                Items:
              </Typography>
              
              {station.items && station.items.length > 0 ? (
                station.items.map((item, itemIndex) => (
                  <Chip 
                    key={itemIndex}
                    label={item}
                    size="small"
                    variant="outlined"
                    onDelete={() => onChange(index, 'items', station.items.filter((_, idx) => idx !== itemIndex))}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No items added
                </Typography>
              )}
              
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => onOpenItemsDialog(index)}
                sx={{ ml: 'auto' }}
              >
                Edit Items
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
});

export default AidStationItem; 